import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { type Container } from "@/builder/types";
import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminCheckinForm } from "../types";

export type CheckinFormList = Paginate<AdminCheckinForm>;

export const checkinForms = createQueryKeys("admin-performance-checkin-forms", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "";

export const fetchCheckinFormsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...checkinForms.all(page, search),
    queryFn: () =>
      adminApi
        .get<CheckinFormList>(`/performance/objective-period-checkin-forms`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchCheckinForms = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCheckinFormsQueryOptions(page, search));

export const fetchCheckinFormQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...checkinForms.one(id),
    queryFn: () =>
      adminApi
        .get<
          AdminCheckinForm<true>
        >(`/performance/objective-period-checkin-forms/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchCheckinForm = (id: number | undefined) =>
  useQuery(fetchCheckinFormQueryOptions(id));

export interface CheckinFormRequest {
  name: string;
  containers: Container[];
}

export const useCreateCheckinForm = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CheckinFormRequest) =>
      adminApi
        .post<
          AdminCheckinForm<true>
        >(`/performance/objective-period-checkin-forms?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: checkinForms.all._def,
      });
    },
  });
};

export const useUpdateCheckinForm = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CheckinFormRequest) =>
      adminApi
        .put<
          AdminCheckinForm<true>
        >(`/performance/objective-period-checkin-forms/${id}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: checkinForms.all._def,
      });
      queryClient.invalidateQueries(checkinForms.one(id));
    },
  });
};

export const useCreateOrUpdateCheckinForm = (id: number | undefined) => {
  const createCheckinForm = useCreateCheckinForm();
  const updateCheckinForm = useUpdateCheckinForm(id!);
  return id ? updateCheckinForm : createCheckinForm;
};

export const useDeleteCheckinForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.delete(`/performance/objective-period-checkin-forms/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: checkinForms.all._def,
      });
      queryClient.invalidateQueries(checkinForms.one(id));
    },
  });
};
